import * as React from "react";

import { Button } from "@libs/zapi-ui/ds/components/button";
import { useDirection } from "@libs/zapi-ui/ds/components/direction";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@libs/zapi-ui/ds/components/field";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@libs/zapi-ui/ds/components/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from "@libs/zapi-ui/ds/components/sheet";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { TeamMemberIdentity, TeamMemberStatusBadge, type TeamMember } from "./TeamMembers";

type MemberAccessOption = { value: string; label: string; description?: string };

type MemberAccessValues = { role: string; accessMode: string; status: string };

type MemberAccessLabels = {
  title: string;
  description: string;
  role: string;
  access: string;
  accessLocked: string;
  status: string;
  save: string;
  cancel: string;
  close: string;
  loading: string;
};

const defaultLabels: MemberAccessLabels = {
  title: "Manage access",
  description: "Change what this person can do in the organization.",
  role: "Role",
  access: "Access",
  accessLocked: "You can’t change this access.",
  status: "Account",
  save: "Save changes",
  cancel: "Cancel",
  close: "Close",
  loading: "Loading"
};

function SelectField({
  label,
  hint,
  value,
  options,
  disabled,
  onValueChange
}: {
  label: string;
  hint?: string;
  value: string;
  options: MemberAccessOption[];
  disabled?: boolean;
  onValueChange: (value: string) => void;
}) {
  const id = React.useId();
  return (
    <Field data-disabled={disabled || undefined}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Select
        items={options}
        value={value}
        disabled={disabled}
        onValueChange={(next) => onValueChange(String(next ?? ""))}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hint && <FieldDescription>{hint}</FieldDescription>}
    </Field>
  );
}

function MemberAccessSheet({
  open,
  onOpenChange,
  member,
  initialValues,
  roles,
  accessModes,
  statuses,
  fixedAccess = {},
  accessLocked = false,
  saving = false,
  labels: labelOverrides,
  onSave
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: TeamMember | null;
  initialValues: MemberAccessValues;
  roles: MemberAccessOption[];
  accessModes: MemberAccessOption[];
  statuses: MemberAccessOption[];
  /** Roles whose access is fixed, by role: the access field hides and takes this value. */
  fixedAccess?: Record<string, string>;
  /** The access field shows but can’t be changed. */
  accessLocked?: boolean;
  saving?: boolean;
  labels?: Partial<MemberAccessLabels>;
  onSave: (values: MemberAccessValues) => void;
}) {
  const labels = { ...defaultLabels, ...labelOverrides };
  const direction = useDirection();
  const [values, setValues] = React.useState(initialValues);
  const [shownFor, setShownFor] = React.useState<string | null>(null);
  const key = open && member ? member.id : null;
  if (key !== shownFor) {
    setShownFor(key);
    if (key) setValues(initialValues);
  }

  const change = (field: keyof MemberAccessValues) => (value: string) =>
    setValues((current) => {
      const next = { ...current, [field]: value };
      if (field === "role" && fixedAccess[value]) next.accessMode = fixedAccess[value];
      return next;
    });

  return (
    <Sheet open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <SheetContent
        closeLabel={labels.close}
        side={direction === "rtl" ? "left" : "right"}
        className="gap-0 bg-canvas p-0 data-[side=left]:w-full data-[side=right]:w-full data-[side=left]:sm:max-w-md data-[side=right]:sm:max-w-md"
      >
        <SheetHeader className="gap-1 px-6 pt-5 pb-4">
          <SheetTitle className="typo-h5">{labels.title}</SheetTitle>
          <SheetDescription>{labels.description}</SheetDescription>
        </SheetHeader>
        {member && (
          <form
            className="flex min-h-0 flex-1 flex-col"
            onSubmit={(event) => {
              event.preventDefault();
              onSave(values);
            }}
          >
            <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 pb-6">
              <div className="flex items-center justify-between gap-3 rounded-xl bg-card p-4 shadow-xs ring-1 ring-foreground/10">
                <TeamMemberIdentity member={member} size="lg" />
                <TeamMemberStatusBadge status={member.status} label={member.statusLabel} />
              </div>
              <FieldGroup className="gap-5">
                <SelectField label={labels.role} value={values.role} options={roles} onValueChange={change("role")} />
                {!fixedAccess[values.role] && (
                  <SelectField
                    label={labels.access}
                    hint={
                      accessLocked
                        ? labels.accessLocked
                        : accessModes.find((mode) => mode.value === values.accessMode)?.description
                    }
                    value={values.accessMode}
                    options={accessModes}
                    disabled={accessLocked}
                    onValueChange={change("accessMode")}
                  />
                )}
                <SelectField
                  label={labels.status}
                  hint={statuses.find((status) => status.value === values.status)?.description}
                  value={values.status}
                  options={statuses}
                  onValueChange={change("status")}
                />
              </FieldGroup>
            </div>
            <SheetFooter className="flex-row justify-end gap-2 border-t bg-background/60 px-6 py-4">
              <Button type="button" variant="ghost" shape="pill" disabled={saving} onClick={() => onOpenChange(false)}>
                {labels.cancel}
              </Button>
              <Button type="submit" shape="pill" disabled={saving}>
                {saving && <Spinner data-icon="inline-start" aria-label={labels.loading} />}
                {labels.save}
              </Button>
            </SheetFooter>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
}

export { MemberAccessSheet, type MemberAccessLabels, type MemberAccessOption, type MemberAccessValues };
